import type React from 'react';
import { calculatePlan } from '../../api/calculate';
import { buildApiPayload } from '../../utils/formPayload';
import { addPetToSubscription, AddPetLine } from '../../services/customerAccount';
import { clearAddonSession } from '../../hooks/useAddonMode';
import { clearDraft } from '../../utils/localStorageDraft';
import type { AppConfigResponse } from '../../types/config';
import type { FormDataSubset } from './checkoutTypes';

interface AddonSubmitParams {
  subscriptionId: string;
  formData: FormDataSubset;
  normalizedSelectedRecipeIds: string[];
  config: AppConfigResponse;
  mountedRef: React.MutableRefObject<boolean>;
  onError: (msg: string) => void;
}

export async function runAddonSubmit({
  subscriptionId,
  formData,
  normalizedSelectedRecipeIds,
  config,
  mountedRef,
  onError,
}: AddonSubmitParams): Promise<void> {
  try {
    // El backend es la única fuente de verdad para el cálculo nutricional.
    // No existe fallback a la aproximación local: si el backend falla, el
    // guardado se bloquea y onError informa al usuario.
    const plan = await calculatePlan(
      // FormDataSubset usa `string` para gender, pero buildApiPayload acepta
      // Partial<DogFormData>. El cast es seguro: los valores reales son los
      // mismos literales que el enum Gender.
      buildApiPayload(formData as Parameters<typeof buildApiPayload>[0], {
        selectedRecipes: normalizedSelectedRecipeIds,
      }),
    );

    const recipeLines: AddPetLine[] = plan.recipes
      .filter((r) => r.monthly_cans > 0)
      .map((r) => ({
        variantId: r.external_variant_id,
        quantity: r.monthly_cans,
        currentPrice: r.price_per_can,
      }));

    // Mi Cuenta / Appstle sigue en variant id (contrato /account/* diferido a #36).
    // El funnel selecciona suplementos por UUID canónico, así que resolvemos cada id
    // (UUID o variant) al variant de Shopify que la línea del contrato espera.
    const supplements = config.supplement_rules?.supplements || [];
    const supplementLines: AddPetLine[] = (formData.selectedSupplementVariantIds || []).map((id) => {
      const sup = supplements.find((s) => s.id === id || s.external_variant_id === id);
      return { variantId: sup?.external_variant_id || id, quantity: 1, currentPrice: sup?.price_eur ?? 0 };
    });

    const lines: AddPetLine[] = [...recipeLines, ...supplementLines].filter((l) => l.variantId && l.quantity > 0);

    await addPetToSubscription(subscriptionId, {
      name: formData.nombre || 'Mi peludo',
      breedKey: formData.raza || undefined,
      breedDisplayName:
        (formData.raza ? config.breed_database?.breeds?.[formData.raza]?.display_name : undefined) || undefined,
      weightKg: formData.peso || undefined,
      ageMonths: formData.ageTotalMonths || undefined,
      ageCategory: formData.ageCategory || undefined,
      bodyCondition: formData.bodyCondition || undefined,
      activityLevel: formData.activityLevel || undefined,
      gender: formData.gender || undefined,
      isNeutered: formData.isNeutered ?? undefined,
      pathologies: formData.pathologies || [],
      allergies: formData.proteinAllergies || [],
      dietType: formData.dietType || undefined,
      dailyKcal: Math.round(plan.der),
      lines,
    });

    clearAddonSession();
    // El funnel addon fue escribiendo el draft (myl_form_state_v3) en cada
    // paso: sin limpiarlo, la próxima visita orgánica mostraría "Continuar
    // plan de {perro ya añadido}" → riesgo de alta duplicada.
    clearDraft();
    // Guard de montaje: si el componente se desmontó durante la llamada async,
    // no forzamos la navegación (mismo patrón que runUpdateSubmit).
    if (!mountedRef.current) return;
    window.location.href = `/mi-cuenta?added=1&pet=${encodeURIComponent(formData.nombre || '')}`;
  } catch (err) {
    if (!mountedRef.current) return;
    onError((err as Error).message || 'No pudimos añadir la mascota.');
  }
}
